import { useCallback, useEffect, useMemo, useState } from 'react'
import { Seat } from 'types/Location'

import { generateSelectedSeats } from './utils'

interface AuditoriumConfig {
    rows: number | null
    columns: number | null
    seats: Omit<Seat, 'seat_id'>[]
    middle_column?: number
}

export const useSeatManagement = (initialData: any, onTotalSeatsChange?: (count: number) => void) => {
    const initialAuditorium = useMemo(
        () => ({
            rows: initialData?.auditorium?.rows || null,
            columns: initialData?.auditorium?.columns || null,
            seats: initialData?.auditorium?.seats || [],
        }),
        [initialData],
    )

    const initialGallery = useMemo(
        () => ({
            rows: initialData?.auditorium?.gallery?.rows || null,
            columns: initialData?.auditorium?.gallery?.columns || null,
            seats: initialData?.auditorium?.gallery?.seats || [],
        }),
        [initialData],
    )

    const [auditorium, setAuditorium] = useState<AuditoriumConfig>(initialAuditorium)
    const [gallery, setGallery] = useState<AuditoriumConfig>(initialGallery)

    const updateSeatsBasedOnDimensions = useCallback(
        (rows: number | null, columns: number | null, isGallery: boolean) => {
            if (rows === null || columns === null) return

            const currentConfig = isGallery ? gallery : auditorium
            const seatsAlreadyExist = currentConfig?.rows === rows && currentConfig?.columns === columns && currentConfig?.seats.length > 0

            if (seatsAlreadyExist) return

            const newSeats = generateSelectedSeats(rows, columns)

            if (isGallery) {
                setGallery((prev) => {
                    if (prev.rows === rows && prev.columns === columns && prev.seats.length === newSeats.length) {
                        return prev
                    }
                    return { ...prev, rows, columns, seats: newSeats }
                })
            } else {
                setAuditorium((prev) => {
                    if (prev.rows === rows && prev.columns === columns && prev.seats.length === newSeats.length) {
                        return prev
                    }
                    return { ...prev, rows, columns, seats: newSeats }
                })
            }
        },
        [auditorium, gallery],
    )

    const toggleSeat = (row: number, column: number, isGallery: boolean = false) => {
        const updateSeats = (seats: Omit<Seat, 'seat_id'>[]) => {
            const seatExists = seats?.some((seat) => seat.row === row && seat.column === column)
            if (seatExists) {
                return seats.filter((seat) => !(seat.row === row && seat.column === column))
            } else {
                const newSeat: Omit<Seat, 'seat_id'> = {
                    row,
                    column,
                    gallery_id: null,
                    on_gallery: false,
                }
                return [...seats, newSeat]
            }
        }

        if (isGallery) {
            setGallery((prev) => {
                const updatedSeats = updateSeats(prev.seats)
                if (updatedSeats.length === prev.seats.length) {
                    return prev
                }
                return { ...prev, seats: updatedSeats }
            })
        } else {
            setAuditorium((prev) => {
                const updatedSeats = updateSeats(prev.seats)
                if (updatedSeats.length === prev.seats.length) {
                    return prev
                }
                return { ...prev, seats: updatedSeats }
            })
        }
    }

    const totalSeats = auditorium.seats.length + gallery.seats.length

    useEffect(() => {
        if (onTotalSeatsChange) {
            onTotalSeatsChange(totalSeats)
        }
    }, [totalSeats, onTotalSeatsChange])

    return {
        auditorium,
        gallery,
        totalSeats,
        setAuditorium,
        setGallery,
        updateSeatsBasedOnDimensions,
        toggleSeat,
    }
}
